import * as DOMUtils from "../../frontendlib/dom_utils";
import * as Utils from "../../frontendlib/utils";

const TEMPLATE = `<div class="last-chance-endscreen">
  <svg class="back-cta-icon hidden" width="24px" height="24px" fill="white">
    <path d="M12 17.414 3.293 8.707l1.414-1.414L12 14.586l7.293-7.293 1.414 1.414L12 17.414z"/>
  </svg>
  <div class="last-chance-endscreen-container">
    <div class="inner-container hidden">
      <img class="last-chance-logo" />
      <div class="icon-container"></div>
      <div class="caption"></div>
      <a class="liftoff-link liftoff-cta">
        <img />
        <div class="common-cta"></div>
      </a>
    </div>
  </div>
</div>`;

/**
 * @typedef AppStoreData
 * @property {string} icon - application icon.
 * @property {string} name - application name.
 * @property {string} company - application company/developer.
 * @property {number} rating - application rating on Google Play store.
 * @property {number} rating_count - number of times the app has been rated on
 * the Google Play store.
 * @property {string | number} download_count - number of times the app has been
 * downloaded from the Google Play store.
 * @property {string} content_rating - app content rating on the Google Play
 * store.
 * @property {string} screenshots - screenshots available for the app.
 * @property {string} description - app description.
 */

/**
 * Creates a Liftoff Last Chance Endscreen component which is displayed to the
 * user as an overlay HTML endscreen.
 *
 * @see https://docs.google.com/presentation/d/15EUBJUVXP2DpFbkA3hB0yE8z-iEy6C1I5tlZ-1h-jBw/edit?usp=sharing
 */
export default class LastChanceEndscreen {
  /**
   *
   * @param {AppStoreData} appStoreData
   * @param {Object} parentEl
   * @param {function} handleClick
   * @param {boolean} validateUserClick - if true then CTA is only triggered if
   * click is invoked by the user and not programmatically.
   * @param {Object} env
   */
  constructor(
    appStoreData,
    parentEl,
    handleClick,
    validateUserClick = false,
    env
  ) {
    this.appStoreData = appStoreData;
    this.parentEl = parentEl;
    this.el = DOMUtils.strToElement(TEMPLATE);
    this.handleClick = handleClick;
    this.validateUserClick = validateUserClick;
    this.env = env;
    this.ctaText = env.cta_text || "INSTALL";
    this.autoEndCardCallCount = 0;
    this.timeout = null;

    if (this.parentEl == null) {
      console.error(
        "Last chance endscreen was created without a parent container"
      );
    }
    // Load app icon when the Liftoff Last Chance Endscreen instance is created.
    this.images = this.loadImages([this.appStoreData.icon]);
  }

  loadImages(imagePaths) {
    return Utils.loadImages(imagePaths);
  }

  prepareAutoEndcard() {
    this.el.querySelector(".caption").innerHTML = this.appStoreData.name;
    this.el.querySelector(".common-cta").innerHTML = this.ctaText.toUpperCase();
    // Show all elements in the LO last chance endscreen after app icon is
    // loaded on the browser.
    this.images
      .then((values) => {
        values.forEach((img) => {
          img.classList.add("icon");
          this.el.querySelector(".icon-container").appendChild(img);
        });
      })
      .catch((error) => {
        console.error(`Error while rendering app icon: ${error.message}`);
      })
      .finally(() => {
        // This will run on success or error.
        this.el.querySelector(".inner-container").classList.remove("hidden");
      });

    this.parentEl.appendChild(this.el);
  }

  showAutoEndcard() {
    if (this.env.has_html_endscreen) {
      if (this.env.platform) {
        // For adding iOS/Android specific styles on the AutoEndcard
        this.el.closest("#liftoff-snippet").classList.add(this.env.platform);
      }

      this.el.querySelector(".back-cta-icon").classList.remove("hidden");
      this.el.classList.add("rolling-up-animation");
    }
  }

  initAutoEndcard() {
    if (this.wasShown) return;
    this.wasShown = true;
    this.prepareAutoEndcard();
    this.showAutoEndcard();
    this.initializeEvents();
  }

  initializeEvents() {
    let touchstart = 0;

    // Fire CTA if user clicks anywhere on the screen.
    this.el.addEventListener("click", (e) => {
      if (!e.target.closest(".back-cta-icon")) {
        const triggerCTA = this.validateUserClick ? e.isTrusted : true;
        if (triggerCTA) {
          this.handleClick(
            !this.useInteractionMonitor ? e : this._lastInteraction
          );
        }
      }
    });

    this.el.querySelector(".back-cta-icon").addEventListener("click", () => {
      this.el.classList.add("rolling-down-animation");
    });

    this.el.addEventListener("touchstart", (e) => {
      touchstart = e.changedTouches[0].screenY;
    });

    this.el.addEventListener("touchend", (e) => {
      // Adding 70px buffer to avoid accidental taps on screen
      if (e.changedTouches[0].screenY > touchstart + 70) {
        this.el.classList.add("rolling-down-animation");
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/js/lib/last_chance_endscreen/last_chance_endscreen.js